import React, { useState, useCallback } from 'react'
import { useStore, useEffectsState } from '../../state/store'
import type {
  EffectType,
  VignetteConfig,
  NoiseConfig,
  BeamConfig,
  IntervalsConfig,
  WavesConfig,
  MatrixConfig,
  GlitchConfig,
  CRTConfig,
} from '../../core/effects/types'
import { EFFECT_LIST, EFFECT_LABELS } from '../../core/effects/types'
import CollapsibleSection from './CollapsibleSection'

interface SliderProps {
  label: string
  value: number
  min: number
  max: number
  step: number
  onChange: (value: number) => void
}

const Slider: React.FC<SliderProps> = ({ label, value, min, max, step, onChange }) => (
  <div className="slider-container">
    <span className="slider-label">{label}</span>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="flex-1"
    />
    <span className="slider-value">{value.toFixed(step < 1 ? 2 : 0)}</span>
  </div>
)

interface ToggleProps {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}

const Toggle: React.FC<ToggleProps> = ({ label, checked, onChange }) => (
  <div className="xp-checkbox-row">
    <span className="xp-field-label">{label}</span>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
  </div>
)

interface SelectProps {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
}

const Select: React.FC<SelectProps> = ({ label, value, options, onChange }) => (
  <div className="xp-checkbox-row">
    <span className="xp-field-label">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-36"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
)

const VignettePanel: React.FC<{ config: VignetteConfig; onChange: (config: Partial<VignetteConfig>) => void }> = ({ config, onChange }) => (
  <div className="space-y-2 mt-2">
    <Slider label="强度" value={config.strength} min={0} max={1} step={0.01} onChange={(v) => onChange({ strength: v })} />
    <Slider label="范围" value={config.radius} min={0.3} max={1} step={0.01} onChange={(v) => onChange({ radius: v })} />
    <Slider label="柔和度" value={config.softness} min={0.1} max={0.5} step={0.01} onChange={(v) => onChange({ softness: v })} />
  </div>
)

const NoisePanel: React.FC<{ config: NoiseConfig; onChange: (config: Partial<NoiseConfig>) => void }> = ({ config, onChange }) => (
  <div className="space-y-2 mt-2">
    <Slider label="强度" value={config.strength} min={0} max={1} step={0.01} onChange={(v) => onChange({ strength: v })} />
    <Slider label="颗粒大小" value={config.scale} min={1} max={10} step={1} onChange={(v) => onChange({ scale: v })} />
    <Slider label="密度" value={config.density} min={0.1} max={1} step={0.01} onChange={(v) => onChange({ density: v })} />
    <Toggle label="彩色噪点" checked={config.colored} onChange={(v) => onChange({ colored: v })} />
  </div>
)

const BeamPanel: React.FC<{ config: BeamConfig; onChange: (config: Partial<BeamConfig>) => void }> = ({ config, onChange }) => (
  <div className="space-y-2 mt-2">
    <Slider label="亮度" value={config.strength} min={0} max={1} step={0.01} onChange={(v) => onChange({ strength: v })} />
    <Slider label="宽度" value={config.width} min={10} max={100} step={1} onChange={(v) => onChange({ width: v })} />
    <Slider label="速度" value={config.speed} min={0.1} max={10} step={0.1} onChange={(v) => onChange({ speed: v })} />
    <Select
      label="方向"
      value={config.direction}
      options={[
        { value: 'left', label: '向左' },
        { value: 'right', label: '向右' },
        { value: 'up', label: '向上' },
        { value: 'down', label: '向下' },
      ]}
      onChange={(v) => onChange({ direction: v as any })}
    />
  </div>
)

const IntervalsPanel: React.FC<{ config: IntervalsConfig; onChange: (config: Partial<IntervalsConfig>) => void }> = ({ config, onChange }) => (
  <div className="space-y-2 mt-2">
    <div className="xp-group-label">第一组线条</div>
    <Slider label="间距" value={config.spacing1} min={5} max={50} step={1} onChange={(v) => onChange({ spacing1: v })} />
    <Slider label="宽度" value={config.width1} min={1} max={10} step={1} onChange={(v) => onChange({ width1: v })} />
    <div className="xp-group-label mt-2">第二组线条</div>
    <Slider label="间距" value={config.spacing2} min={5} max={50} step={1} onChange={(v) => onChange({ spacing2: v })} />
    <Slider label="宽度" value={config.width2} min={1} max={10} step={1} onChange={(v) => onChange({ width2: v })} />
    <Select
      label="方向"
      value={config.direction}
      options={[
        { value: 'horizontal', label: '水平' },
        { value: 'vertical', label: '垂直' },
      ]}
      onChange={(v) => onChange({ direction: v as any })}
    />
    <Toggle label="启用动画" checked={config.animated} onChange={(v) => onChange({ animated: v })} />
  </div>
)

const WavesPanel: React.FC<{ config: WavesConfig; onChange: (config: Partial<WavesConfig>) => void }> = ({ config, onChange }) => (
  <div className="space-y-2 mt-2">
    <Slider label="强度" value={config.strength} min={0} max={1} step={0.01} onChange={(v) => onChange({ strength: v })} />
    <Slider label="频率" value={config.frequency} min={0.01} max={0.2} step={0.01} onChange={(v) => onChange({ frequency: v })} />
    <Slider label="速度" value={config.speed} min={0.1} max={5} step={0.1} onChange={(v) => onChange({ speed: v })} />
    <Slider label="色相偏移" value={config.hueShift} min={0} max={360} step={1} onChange={(v) => onChange({ hueShift: v })} />
    <Slider label="波源数量" value={config.waveCount} min={2} max={5} step={1} onChange={(v) => onChange({ waveCount: v })} />
  </div>
)

const MatrixPanel: React.FC<{ config: MatrixConfig; onChange: (config: Partial<MatrixConfig>) => void }> = ({ config, onChange }) => (
  <div className="space-y-2 mt-2">
    <Slider label="亮度" value={config.strength} min={0} max={1} step={0.01} onChange={(v) => onChange({ strength: v })} />
    <Slider label="速度" value={config.speed} min={0.5} max={5} step={0.1} onChange={(v) => onChange({ speed: v })} />
    <Slider label="字符大小" value={config.charSize} min={10} max={30} step={1} onChange={(v) => onChange({ charSize: v })} />
    <Slider label="拖尾长度" value={config.trailLength} min={5} max={30} step={1} onChange={(v) => onChange({ trailLength: v })} />
    <Slider label="密度" value={config.density} min={0.1} max={1} step={0.01} onChange={(v) => onChange({ density: v })} />
    <Select
      label="模式"
      value={config.mode}
      options={[
        { value: 'overlay', label: '叠加' },
        { value: 'replace', label: '替换' },
      ]}
      onChange={(v) => onChange({ mode: v as 'overlay' | 'replace' })}
    />
  </div>
)

const GlitchPanel: React.FC<{ config: GlitchConfig; onChange: (config: Partial<GlitchConfig>) => void }> = ({ config, onChange }) => (
  <div className="space-y-2 mt-2">
    <Slider label="RGB 位移" value={config.rgbShift} min={0} max={20} step={1} onChange={(v) => onChange({ rgbShift: v })} />
    <Slider label="块位移" value={config.blockShift} min={0} max={50} step={1} onChange={(v) => onChange({ blockShift: v })} />
    <Slider label="块数量" value={config.blockCount} min={0} max={10} step={1} onChange={(v) => onChange({ blockCount: v })} />
    <Slider label="变化频率" value={config.speed} min={0.1} max={5} step={0.1} onChange={(v) => onChange({ speed: v })} />
    <Select
      label="方向"
      value={config.direction}
      options={[
        { value: 'left', label: '向左' },
        { value: 'right', label: '向右' },
      ]}
      onChange={(v) => onChange({ direction: v as any })}
    />
  </div>
)

const CRTPanel: React.FC<{ config: CRTConfig; onChange: (config: Partial<CRTConfig>) => void }> = ({ config, onChange }) => (
  <div className="space-y-2 mt-2">
    <Slider label="扫描线强度" value={config.scanlineStrength} min={0} max={1} step={0.01} onChange={(v) => onChange({ scanlineStrength: v })} />
    <Slider label="扫描线间距" value={config.scanlineSpacing} min={2} max={4} step={1} onChange={(v) => onChange({ scanlineSpacing: v })} />
    <Slider label="屏幕弯曲" value={config.curvature} min={0} max={0.5} step={0.01} onChange={(v) => onChange({ curvature: v })} />
    <Slider label="边缘色散" value={config.edgeRgbShift} min={0} max={10} step={1} onChange={(v) => onChange({ edgeRgbShift: v })} />
    <Slider label="CRT 暗角" value={config.crtVignette} min={0} max={1} step={0.01} onChange={(v) => onChange({ crtVignette: v })} />
  </div>
)

const EffectsPanel: React.FC = () => {
  const [expandedEffect, setExpandedEffect] = useState<EffectType | null>(null)
  const effectsState = useEffectsState()
  const { setEffectEnabled, updateEffectConfig, setEffectsOrder, resetEffectsState } = useStore()

  const moveEffectUp = useCallback((effectType: EffectType) => {
    const index = effectsState.order.indexOf(effectType)
    if (index > 0) {
      const newOrder = [...effectsState.order]
      ;[newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]]
      setEffectsOrder(newOrder)
    }
  }, [effectsState.order, setEffectsOrder])

  const moveEffectDown = useCallback((effectType: EffectType) => {
    const index = effectsState.order.indexOf(effectType)
    if (index < effectsState.order.length - 1) {
      const newOrder = [...effectsState.order]
      ;[newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]]
      setEffectsOrder(newOrder)
    }
  }, [effectsState.order, setEffectsOrder])

  const toggleEffect = useCallback((effectType: EffectType, enabled: boolean) => {
    setEffectEnabled(effectType, enabled)
  }, [setEffectEnabled])

  const handleUpdateConfig = useCallback((effectType: EffectType, config: Partial<any>) => {
    updateEffectConfig(effectType, config)
  }, [updateEffectConfig])

  const getEffectPanel = (effectType: EffectType) => {
    const config = effectsState[effectType].config
    const onChange = (cfg: Partial<any>) => handleUpdateConfig(effectType, cfg)

    switch (effectType) {
      case 'vignette': return <VignettePanel config={config as VignetteConfig} onChange={onChange} />
      case 'noise': return <NoisePanel config={config as NoiseConfig} onChange={onChange} />
      case 'beam': return <BeamPanel config={config as BeamConfig} onChange={onChange} />
      case 'intervals': return <IntervalsPanel config={config as IntervalsConfig} onChange={onChange} />
      case 'waves': return <WavesPanel config={config as WavesConfig} onChange={onChange} />
      case 'matrix': return <MatrixPanel config={config as MatrixConfig} onChange={onChange} />
      case 'glitch': return <GlitchPanel config={config as GlitchConfig} onChange={onChange} />
      case 'crt': return <CRTPanel config={config as CRTConfig} onChange={onChange} />
      default: return null
    }
  }

  const applyPreset = useCallback((preset: 'retro' | 'glitch' | 'matrix' | 'cinema' | 'clear') => {
    switch (preset) {
      case 'retro':
        setEffectEnabled('crt', true)
        setEffectEnabled('vignette', true)
        break
      case 'glitch':
        setEffectEnabled('glitch', true)
        setEffectEnabled('noise', true)
        break
      case 'matrix':
        setEffectEnabled('matrix', true)
        break
      case 'cinema':
        setEffectEnabled('vignette', true)
        setEffectEnabled('beam', true)
        break
      case 'clear':
        EFFECT_LIST.forEach((e) => setEffectEnabled(e, false))
        break
    }
  }, [setEffectEnabled])

  return (
    <CollapsibleSection title="特效设置" icon="FX" defaultExpanded={false}>
      <div className="space-y-2">
        {effectsState.order.map((effectType, index) => {
          const effectState = effectsState[effectType]
          const isExpanded = expandedEffect === effectType

          return (
            <div
              key={effectType}
              className={`xp-list-item ${effectState.enabled ? 'xp-list-item-active' : ''}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  <div className="flex flex-col gap-1 pt-0.5">
                    <button
                      onClick={() => moveEffectUp(effectType)}
                      disabled={index === 0}
                      className="xp-btn xp-btn-small min-w-[20px]"
                    >
                      ^
                    </button>
                    <button
                      onClick={() => moveEffectDown(effectType)}
                      disabled={index === effectsState.order.length - 1}
                      className="xp-btn xp-btn-small min-w-[20px]"
                    >
                      v
                    </button>
                  </div>

                  <input
                    type="checkbox"
                    checked={effectState.enabled}
                    onChange={(e) => toggleEffect(effectType, e.target.checked)}
                    className="mt-1"
                  />

                  <button
                    onClick={() => setExpandedEffect(isExpanded ? null : effectType)}
                    className="text-left flex-1 min-w-0"
                  >
                    <span className="block text-[16px] font-bold">{EFFECT_LABELS[effectType]}</span>
                    <span className="block text-[15px] xp-muted">
                      顺序 {index + 1} / {effectsState.order.length}
                    </span>
                  </button>
                </div>

                <button
                  onClick={() => setExpandedEffect(isExpanded ? null : effectType)}
                  className="xp-btn xp-btn-small min-w-[24px]"
                >
                  {isExpanded ? '-' : '+'}
                </button>
              </div>

              {isExpanded && effectState.enabled && (
                <div className="mt-3 border-t border-[#a7a39a] pt-2">
                  {getEffectPanel(effectType)}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div className="mt-3 space-y-2">
        <div className="xp-group-label">快捷预设</div>
        <div className="xp-toolbar">
          <button onClick={() => applyPreset('retro')} className="xp-btn xp-btn-small">Retro CRT</button>
          <button onClick={() => applyPreset('glitch')} className="xp-btn xp-btn-small">Glitch</button>
          <button onClick={() => applyPreset('matrix')} className="xp-btn xp-btn-small">Matrix</button>
          <button onClick={() => applyPreset('cinema')} className="xp-btn xp-btn-small">Cinema</button>
          <button onClick={() => applyPreset('clear')} className="xp-btn xp-btn-small">清除全部</button>
        </div>
      </div>

      <button
        onClick={resetEffectsState}
        className="btn-secondary w-full mt-3"
      >
        重置特效参数
      </button>
    </CollapsibleSection>
  )
}

export default EffectsPanel
